import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { cn } from '../lib/cn'
import { microsoftStatusQuery } from '../lib/queries'
import { trpc } from '../lib/trpc'
import { MicrosoftAnleitung } from './MicrosoftAnleitung'

/**
 * **Das eigene Microsoft-Konto – verbinden, sehen, trennen**
 * ([Z-17](../../../../docs/phasen/zusatz-17-outlook.md)).
 *
 * Steht in der Seitenleiste bei der Person, neben Erinnerung und Vorwarnung: es ist **ihre**
 * Verbindung, nicht eine Einstellung der Administration. Ein Mit-Owner, der „Einstellungen" nie
 * sieht, soll sie trotzdem herstellen können.
 *
 * Drei Zustände in einer Zeile: *nicht verbunden* (ein Knopf), *verbunden* (Konto, letzter
 * Abgleich, „jetzt", „trennen"), *abgelaufen oder gestört* (der Satz aus dem Abgleich und der
 * Knopf zum Neuverbinden). Was schiefging, steht in Worten – ein Token, das nach 90 Tagen Pause
 * abgelaufen ist, darf nicht nur im Protokoll stehen.
 *
 * Der Rückweg von Microsoft landet auf `/?microsoft=…`; das liest diese Komponente einmal und
 * räumt es weg, damit ein Neuladen die Meldung nicht wiederholt.
 */
export function MicrosoftVerbindung() {
  const queryClient = useQueryClient()
  const { data } = useQuery(microsoftStatusQuery)
  const [rueckweg, setRueckweg] = useState<string | null>(null)
  const [anleitungOffen, setAnleitungOffen] = useState(false)

  useEffect(() => {
    const adresse = new URL(window.location.href)
    const wert = adresse.searchParams.get('microsoft')
    if (!wert) return
    setRueckweg(wert === 'fehler' ? `fehler:${adresse.searchParams.get('grund') ?? ''}` : wert)
    adresse.searchParams.delete('microsoft')
    adresse.searchParams.delete('grund')
    window.history.replaceState(window.history.state, '', adresse)
    void queryClient.invalidateQueries({ queryKey: ['microsoft'] })
  }, [queryClient])

  const trennen = useMutation({
    mutationFn: () => trpc.microsoft.trennen.mutate(),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['microsoft'] }),
  })
  const abgleichen = useMutation({
    mutationFn: () => trpc.microsoft.abgleichen.mutate(),
    /** Der Job läuft ein paar Sekunden; danach den Stand holen, damit „zuletzt" stimmt. */
    onSettled: () => {
      window.setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey: ['microsoft'] })
        void queryClient.invalidateQueries({ queryKey: ['tasks'] })
      }, 6000)
    },
  })

  if (!data) return null

  const v = data.verbindung
  const gestoert = v && (!v.aktiv || v.letzterFehler)

  return (
    <div className="mt-2 border-t border-line px-2 pt-2 text-[11px]">
      <div className="flex items-center gap-1.5">
        <span className="font-semibold text-ink2">Microsoft 365</span>
        {v && (
          <span className="truncate text-muted" title={v.konto}>
            · {v.konto}
          </span>
        )}
        {/* Die Anleitung steht in Work, nicht in einer Datei – Nils, 15.09.2026: „Die Anleitung muss in Work." */}
        <button
          type="button"
          onClick={() => setAnleitungOffen(true)}
          className="ml-auto shrink-0 cursor-pointer text-muted hover:text-accent-deep hover:underline"
        >
          Wie geht das?
        </button>
      </div>
      {anleitungOffen && (
        <MicrosoftAnleitung
          schliessen={() => setAnleitungOffen(false)}
          verbunden={Boolean(v?.aktiv)}
          eingerichtet={data.eingerichtet}
          todo={Boolean(v?.todo)}
        />
      )}

      {rueckweg && <Rueckmeldung wert={rueckweg} />}

      {!v ? (
        data.eingerichtet ? (
          <a
            href="/api/microsoft/verbinden"
            className="tap mt-1 inline-flex cursor-pointer items-center rounded-lg border border-line2 px-2.5 py-1 font-semibold text-ink2 hover:bg-[#f3f2ef]"
          >
            Mit Microsoft 365 verbinden
          </a>
        ) : (
          <p className="mt-1 text-muted">
            Der Microsoft-Zugang ist noch nicht eingerichtet (KI &amp; Einstellungen → Zugänge).
          </p>
        )
      ) : (
        <>
          {gestoert ? (
            <p className="mt-1 text-warn">{v.letzterFehler ?? 'Die Verbindung ist abgelaufen.'}</p>
          ) : (
            <p className="mt-0.5 text-muted">
              {v.todo
                ? 'Gekennzeichnete Mails und To-Do-Aufgaben werden Aufgaben'
                : 'Gekennzeichnete Mails werden Aufgaben'}
              {data.persoenlicheWelt ? ` in ${data.persoenlicheWelt.label}` : ''}
              {v.letzterAbgleichAm
                ? ` · zuletzt ${uhrzeit(v.letzterAbgleichAm)}`
                : ' · noch kein Abgleich'}
            </p>
          )}
          {!data.persoenlicheWelt && (
            <p className="mt-0.5 text-warn">
              Dir fehlt eine persönliche Arbeitswelt – ohne sie landen die Mails nirgends.
            </p>
          )}
          {v.aktiv && !v.todo && (
            <p className="mt-0.5 text-muted">
              To Do ist noch nicht dabei –{' '}
              <a href="/api/microsoft/verbinden" className="text-accent-deep hover:underline">
                einmal neu verbinden
              </a>
              , dann kommen die Aufgaben aus der To-Do-App mit.
            </p>
          )}
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
            {!v.aktiv ? (
              <a href="/api/microsoft/verbinden" className={knopf}>
                Neu verbinden
              </a>
            ) : (
              <button
                type="button"
                onClick={() => abgleichen.mutate()}
                disabled={abgleichen.isPending}
                className={knopf}
              >
                {abgleichen.isPending ? 'Läuft …' : 'Jetzt abgleichen'}
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                if (window.confirm('Microsoft-Verbindung trennen? Vorhandene Aufgaben bleiben.'))
                  trennen.mutate()
              }}
              disabled={trennen.isPending}
              className={cn(knopf, 'text-muted')}
            >
              Trennen
            </button>
          </div>
        </>
      )}
    </div>
  )
}

const knopf =
  'cursor-pointer text-[11px] font-semibold text-accent-deep hover:underline disabled:cursor-default disabled:opacity-60'

function uhrzeit(wert: Date | string): string {
  const d = new Date(wert)
  const heute = new Date().toDateString() === d.toDateString()
  return heute
    ? d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) +
        ', ' +
        d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
}

function Rueckmeldung({ wert }: { wert: string }) {
  const [art, grund] = wert.split(':')
  const text =
    art === 'verbunden'
      ? 'Verbunden. Der erste Abgleich läuft – gekennzeichnete Mails erscheinen gleich in „Mein Tag".'
      : art === 'abgelehnt'
        ? 'Du hast die Zustimmung bei Microsoft abgebrochen. Nichts wurde verbunden.'
        : art === 'nicht_eingerichtet'
          ? 'Der Microsoft-Zugang ist nicht eingerichtet (KI & Einstellungen → Zugänge).'
          : art === 'state'
            ? 'Der Rückweg von Microsoft passte nicht zu dieser Sitzung – bitte noch einmal verbinden.'
            : art === 'anmeldung'
              ? 'Du warst dabei nicht in Work angemeldet – bitte anmelden und noch einmal verbinden.'
              : `Verbinden fehlgeschlagen${grund ? ` (${grund})` : ''}. Steht eine Berechtigung in Entra noch aus?`
  return (
    <p role="status" className={cn('mt-1', art === 'verbunden' ? 'text-good-text' : 'text-warn')}>
      {text}
    </p>
  )
}
